import { type MultiSelectProps } from "@mantine/core";
import { useCallback } from "react";
import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form";
import { MultiSelect as MantineMultiSelect } from "src/components/shared/MantineCombobox";

interface Props<T extends FieldValues> extends MultiSelectProps {
  name: Path<T>;
  control: Control<T>;
  onChange?: (value: string[]) => void | Promise<void>;
  required?: boolean;
}

function MultiSelect<T extends FieldValues>({
  name,
  control,
  onChange,
  required,
  ...props
}: Props<T>) {
  const {
    field: { onChange: defaultOnChange, ...field },
    fieldState: { invalid },
  } = useController({
    name,
    control,
    rules: { required },
  });

  const handleChange = useCallback(
    async (value: string[]) => {
      defaultOnChange(value);
      if (onChange) await onChange(value);
    },
    [defaultOnChange, onChange],
  );

  return (
    <MantineMultiSelect
      {...field}
      value={field.value ?? []}
      {...props}
      onChange={handleChange}
      error={props.error || invalid}
    />
  );
}

export default MultiSelect;
